import {
  Table, TableBody, TableContainer, TableHead, TableRow, TableCell, Skeleton
} from '@mui/material'
import { Scheme, Grade, Period, Salary } from '../api-client';
import { Column } from '../types'
import { useSalaryData } from '../hooks';

type Props = {
  scheme: Scheme | null
  grade: Grade | null
  period: Period | null
}

const SalaryDataView = (props: Props) => {
  const { data: salaryData = [], isLoading, isPlaceholderData, error } = useSalaryData(
    props.scheme,
    props.period,
    props.grade
  ) as {
    data?: Salary[];
    isLoading: boolean;
    isPlaceholderData: boolean;
    error: Error | null;
  };

  if (error) {
    console.error('Error rendering salary data:', error);
  }

  // Get the selected scheme object to check if it's an exchange scheme
  const isExchangeScheme = !!props.scheme?.is_exchange

  const getColumns = (): Column[] => {
    let cols: Column[] = [
      {id: 'point', label: 'Scale point'},
      {id: 'salary', label: 'Salary (£)'},
    ]

    if (isExchangeScheme) {
      cols.push({id: 'exchange', label: 'Exchange (£)'})
    }

    cols.push(
      {id: 'pension', label: 'Pension (£)'},
      {id: 'ni', label: 'NI (£)'},
      {id: 'levy', label: 'Apprenticeship levy (£)'},
      {id: 'total', label: 'Total(£)'},
    )

    return cols
  }

  return (
    <TableContainer sx={{maxHeight: 700}}>
      <Table stickyHeader data-cy={"salary-table"}>
        <TableHead>
          <TableRow>
            {getColumns().map((column) => (
              <TableCell key={column.id}>{column.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading || isPlaceholderData || error ? (
            Array.from({ length: salaryData.length }).map((_, index) => (
              <TableRow key={index}
                sx={{backgroundColor: index % 2 === 0 ? 'grey.100' : 'white'}}>
                <TableCell><Skeleton width={50} data-testid="skeleton" /></TableCell>
                <TableCell><Skeleton width={50} data-testid="skeleton" /></TableCell>
                {isExchangeScheme && (
                  <TableCell><Skeleton width={50} data-testid="skeleton" /></TableCell>
                )}
                <TableCell><Skeleton width={50} data-testid="skeleton" /></TableCell>
                <TableCell><Skeleton width={50} data-testid="skeleton" /></TableCell>
                <TableCell><Skeleton width={50} data-testid="skeleton" /></TableCell>
              </TableRow>
            ))
          ) : (
            salaryData.map((row, index) => (
              <TableRow key={index}
                sx={{backgroundColor: index % 2 === 0 ? 'grey.100' : 'white'}}>
                <TableCell data-cy={`salary-row-${index}-point`}>{row.point}</TableCell>
                <TableCell data-cy={`salary-row-${index}-salary`}>{row.salary}</TableCell>
                {isExchangeScheme && (
                  <TableCell data-cy={`salary-row-${index}-exchange`}>{row.exchange}</TableCell>
                )}
                <TableCell data-cy={`salary-row-${index}-pension`}>{row.pension}</TableCell>
                <TableCell data-cy={`salary-row-${index}-ni`}>{row.ni}</TableCell>
                <TableCell data-cy={`salary-row-${index}-levy`}>{row.levy}</TableCell>
                <TableCell data-cy={`salary-row-${index}-total`}>{row.total}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default SalaryDataView;
